import { motion } from "framer-motion";
import {
  ShieldCheck,
  IdentificationCard,
  Prohibit,
  HandCoins,
  Scales,
  EnvelopeSimple,
  Phone,
} from "@phosphor-icons/react";
import SEO from "@/components/site/SEO";
import LegalLayout from "@/components/site/LegalLayout";

const sections = [
  {
    icon: ShieldCheck,
    title: "Anti-Money Laundering (AML)",
    body: [
      "Altin Link Danışmanlık Hizmetleri Ltd. Şti. is firmly committed to preventing money laundering and the financing of terrorism. We conduct our brokerage and consulting activities in full compliance with applicable Turkish legislation, including Law No. 5549 on the Prevention of Laundering Proceeds of Crime, and with relevant international standards such as the FATF Recommendations.",
      "We maintain internal controls, risk assessments and ongoing monitoring designed to detect and prevent suspicious activity. Where required by law, we report suspicious transactions to the competent authorities (MASAK) and cooperate fully with regulators and financial institutions.",
    ],
  },
  {
    icon: IdentificationCard,
    title: "Know Your Customer (KYC)",
    body: [
      "Customer due diligence and verification procedures are applied whenever required by law, by our banking partners, or by the nature of a specific engagement. This may include verifying the identity of clients and counterparties, confirming beneficial ownership, and understanding the purpose and intended nature of a business relationship.",
      "We may request supporting documentation — such as company registration records, identification documents and proof of address — and reserve the right to decline or terminate any relationship where satisfactory verification cannot be completed.",
    ],
  },
  {
    icon: Prohibit,
    title: "Sanctions Compliance",
    body: [
      "Altin Link does not conduct business with individuals, entities or countries subject to applicable sanctions. We screen counterparties against relevant sanctions lists, including those maintained by the United Nations, the European Union, the United States (OFAC) and the Republic of Türkiye.",
      "We will not knowingly facilitate any transaction that would breach international sanctions or export-control regulations, and we continuously review our engagements to remain compliant as sanctions regimes evolve.",
    ],
  },
  {
    icon: HandCoins,
    title: "Anti-Bribery & Anti-Corruption",
    body: [
      "We operate a strict zero-tolerance policy towards bribery and corruption in any form. Altin Link, its representatives and its partners are prohibited from offering, giving, requesting or accepting any bribe, kickback or improper advantage — whether directly or through third parties.",
      "This policy applies to interactions with private parties and public officials alike, and to facilitation payments. We expect the same standards from every business partner we introduce or represent, and breaches may result in immediate termination of the relationship and referral to the relevant authorities.",
    ],
  },
  {
    icon: Scales,
    title: "Business Ethics",
    body: [
      "Integrity, transparency, fairness and responsible business conduct are the foundation of every engagement we undertake. We handle confidential information with discretion, disclose conflicts of interest, and treat clients, counterparties and colleagues with respect.",
      "Our commercial recommendations are made in good faith and in the legitimate interest of our clients. We believe ethical business is not only a legal obligation but a long-term commercial advantage that underpins the trust our partners place in us.",
    ],
  },
];

export default function Compliance() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Compliance | Altin Link",
    description:
      "Altin Link's compliance framework covering AML, KYC, sanctions, anti-bribery and business ethics.",
    url: "https://www.altin-link.com/compliance",
  };

  return (
    <>
      <SEO
        title="Compliance"
        description="Altin Link's compliance framework — Anti-Money Laundering (AML), Know Your Customer (KYC), sanctions compliance, anti-bribery & anti-corruption, and business ethics for banks, financial institutions and corporate due diligence."
        path="/compliance"
        schema={schema}
      />
      <LegalLayout
        testid="compliance-page"
        overline="Compliance"
        title="Our Compliance"
        accent="Framework"
        intro="Altin Link maintains a rigorous compliance framework designed to meet the expectations of banks, financial institutions, international partners and corporate due-diligence reviews."
      >
        <div className="grid grid-cols-1 gap-0">
          {sections.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                data-testid={`compliance-section-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5 }}
                className="py-10 border-b border-altin-border"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 grid place-items-center border border-altin-gold text-altin-gold shrink-0">
                    <Icon size={26} weight="light" />
                  </div>
                  <div>
                    <div className="text-xs tracking-[0.25em] uppercase text-altin-gold mb-2">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h2 className="font-serif-display text-2xl md:text-3xl text-altin-ink tracking-tight">
                      {s.title}
                    </h2>
                    <div className="mt-4 space-y-4 text-altin-muted leading-relaxed">
                      {s.body.map((p, k) => (
                        <p key={k}>{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Compliance officer contact */}
        <div
          data-testid="compliance-contact"
          className="mt-12 bg-altin-ink text-white p-8 md:p-12"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-altin-gold mb-3">
            Contact
          </div>
          <h2 className="font-serif-display text-3xl tracking-tight">
            Contact our Compliance Officer
          </h2>
          <p className="mt-4 text-white/70 leading-relaxed max-w-2xl">
            For any compliance-related enquiries, due-diligence requests or to
            request our KYC documentation package, please reach our Compliance
            Officer directly. We respond to institutional requests within one
            business day.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="mailto:compliance@altin-link.com"
              data-testid="compliance-email"
              className="inline-flex items-center gap-3 bg-altin-gold text-altin-ink px-6 py-4 text-xs tracking-[0.2em] uppercase hover:bg-altin-goldHover transition-colors"
            >
              <EnvelopeSimple size={18} weight="light" />
              compliance@altin-link.com
            </a>
            <a
              href="tel:+902128300378"
              className="inline-flex items-center gap-3 border border-white/25 text-white px-6 py-4 text-xs tracking-[0.2em] uppercase hover:border-altin-gold hover:text-altin-gold transition-colors"
            >
              <Phone size={18} weight="light" />
              +90 212 830 03 78
            </a>
          </div>
        </div>
      </LegalLayout>
    </>
  );
}
